import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { clamp, remap } from '../../../utils/math';
import { RangeSliderOption } from '@immosky/types';

@Component({
  selector: 'lib-range-slider-knob',
  templateUrl: './range-slider-knob.component.html',
  styleUrls: ['./range-slider-knob.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RangeSliderKnobComponent {
  @Input()
  sliderWidth = 0;

  @Input()
  sliderLeft = 0;

  @Input()
  sliderRight = 0;

  @Input()
  options: RangeSliderOption[] = [];

  @Input()
  currentOption!: RangeSliderOption;

  @Output()
  update = new EventEmitter<RangeSliderOption>();

  @Output()
  updateDone = new EventEmitter<RangeSliderOption>();

  @ViewChild('knob')
  knob!: ElementRef<HTMLElement>;

  dragState = {
    isDragging: false,
    startX: 0,
    distanceX: 0,
  };

  get transform(): string {
    const safeX = clamp(this.minX, this.x, this.maxX);
    return `translateX(${safeX}px)`;
  }

  get x(): number {
    return remap(
      this.minIndex,
      this.maxIndex,
      this.currentIndex,
      this.minX,
      this.maxX
    );
  }

  get dragX(): number {
    return this.dragState.startX + this.dragState.distanceX;
  }

  get minIndex(): number {
    return 0;
  }

  get maxIndex(): number {
    return this.options.length - 1;
  }

  get minX(): number {
    return 0;
  }

  get maxX(): number {
    return this.sliderWidth - this.knobWidth;
  }

  get knobWidth(): number {
    return this.knob?.nativeElement.clientWidth || 0;
  }

  get currentIndex(): number {
    return this.options.findIndex((option: RangeSliderOption) => {
      return option.value === (this.currentOption?.value || '');
    });
  }

  constructor(private cdRef: ChangeDetectorRef) {}

  onDragStart(event: MouseEvent | TouchEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.dragState.isDragging = true;
    this.dragState.startX = this.clientX(event);
    this.dragState.distanceX = 0;
    this.cdRef.markForCheck();
  }

  @HostListener('window:mousemove', ['$event'])
  @HostListener('window:touchmove', ['$event'])
  onDragMove(event: MouseEvent | TouchEvent) {
    if (this.dragState.isDragging) {
      this.dragState.distanceX = this.clientX(event) - this.dragState.startX;
      const option = this.findClosestOption();
      this.update.emit(option);
      this.cdRef.markForCheck();
    }
  }

  private clientX(event: MouseEvent | TouchEvent): number {
    if (event instanceof MouseEvent) {
      return event.clientX;
    } else if (event instanceof TouchEvent) {
      return event.touches[0].clientX;
    }
    return 0;
  }

  @HostListener('window:mouseup', ['$event'])
  @HostListener('window:touchend', ['$event'])
  onDragEnd(event: MouseEvent | TouchEvent) {
    if (this.dragState.isDragging) {
      this.dragState.isDragging = false;
      const option = this.findClosestOption();
      this.updateDone.emit(option);
      this.cdRef.markForCheck();
    }
  }

  private findClosestOption(): RangeSliderOption {
    const closestIndex = Math.round(
      remap(this.sliderLeft, this.sliderRight, this.dragX, 0, this.maxIndex)
    );
    const safeIndex = clamp(0, closestIndex, this.maxIndex);
    return this.options[safeIndex];
  }
}
